// Session Security Utilities
import { encryptionUtils } from './encryption';
import logger from './logger';

const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes
const MAX_SESSION_DURATION = 8 * 60 * 60 * 1000; // 8 hours

export const sessionUtils = {
    // Generate cryptographically secure session token
    generateSessionToken: () => {
        return encryptionUtils.generateSecureToken(64);
    },

    // Validate session on server side
    validateSession: async (token) => {
        try {
            if (!token || typeof token !== 'string' || token.length < 32) {
                return false;
            }
            
            // TODO: Implement actual server-side validation with Firebase Functions
            // For now, perform basic client-side checks
            const sessionData = sessionUtils.getSessionData();
            if (!sessionData) {
                return false;
            }
            
            // Check if session is expired
            const now = Date.now();
            return sessionData.expiry > now && sessionData.maxExpiry > now;
        } catch (error) {
            logger.error('Session validation failed:', error);
            return false;
        }
    },

    // Secure session storage with encryption
    setSecureSession: (token, userId, expiry = SESSION_TIMEOUT) => {
        const sessionData = {
            token: encryptionUtils.hashForLogs(token), // Store hash, not actual token
            userId,
            expiry: Date.now() + expiry,
            created: Date.now(),
            maxExpiry: Date.now() + MAX_SESSION_DURATION,
            lastActivity: Date.now()
        };
        
        try {
            // Store encrypted session data
            const encrypted = encryptionUtils.encrypt(sessionData);
            if (encrypted) {
                sessionStorage.setItem('secure_session', encrypted);
                return true;
            }
        } catch (error) {
            logger.error('Failed to set secure session:', error);
        }
        return false;
    },

    // Check if session is valid with activity tracking
    isSessionValid: () => {
        try {
            const encryptedSession = sessionStorage.getItem('secure_session');
            if (!encryptedSession) return false;
            
            const sessionData = encryptionUtils.decrypt(encryptedSession);
            if (!sessionData) return false;
            
            const now = Date.now();
            
            // Check if session expired or reached max duration
            if (sessionData.expiry < now || sessionData.maxExpiry < now) {
                sessionUtils.clearSession();
                return false;
            }
            
            // Update last activity
            sessionData.lastActivity = now;
            sessionData.expiry = now + SESSION_TIMEOUT;
            
            const encrypted = encryptionUtils.encrypt(sessionData);
            if (encrypted) {
                sessionStorage.setItem('secure_session', encrypted);
            }
            
            return true;
        } catch (error) {
            logger.error('Session validation error:', error);
            sessionUtils.clearSession();
            return false;
        }
    },

    // Get session data securely
    getSessionData: () => {
        try {
            const encryptedSession = sessionStorage.getItem('secure_session');
            if (!encryptedSession) return null;
            
            return encryptionUtils.decrypt(encryptedSession);
        } catch (error) {
            logger.error('Failed to get session data:', error);
            return null;
        }
    },

    // Get session token for CSRF validation
    getSessionToken: () => {
        const sessionData = sessionUtils.getSessionData();
        return sessionData?.token || null;
    },

    // Get stored UID for validation
    getStoredUid: () => {
        const sessionData = sessionUtils.getSessionData();
        return sessionData?.userId || null;
    },

    // Clear session and related data
    clearSession: () => {
        try {
            sessionStorage.removeItem('secure_session');
            localStorage.removeItem('user_preferences');
            // Clear any other sensitive data
        } catch (error) {
            logger.error('Failed to clear session:', error);
        }
    },

    // Check for suspicious activity
    detectSuspiciousActivity: () => {
        const sessionData = sessionUtils.getSessionData();
        if (!sessionData) return false;
        
        const now = Date.now();
        const timeSinceCreation = now - sessionData.created;
        const timeSinceActivity = now - sessionData.lastActivity;
        
        // Flag if session is very old or inactive for too long
        return timeSinceCreation > MAX_SESSION_DURATION || 
               timeSinceActivity > SESSION_TIMEOUT * 2;
    }
};